// src/pages/Dashboard.jsx
import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CardActionArea,
  Box,
  Skeleton,
  LinearProgress,
  Chip
} from '@mui/material';
import { FolderOpen, Description, DateRange, CheckCircle } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { supabase } from '../supabaseClient';

function Dashboard() {
  const { user, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  
  // Refs for managing state and preventing race conditions
  const isLoadingData = useRef(false);
  const lastVisibilityChange = useRef(Date.now());
  const cachedProjects = useRef(null);
  const globalTimeoutId = useRef(null);
  const fetchInProgress = useRef(false);

  // Store projects in cache when we get fresh data
  useEffect(() => {
    if (projects.length > 0) {
      cachedProjects.current = projects;
    }
  }, [projects]);

  // Helper function to reset connection state
  const resetConnection = useCallback(() => {
    isLoadingData.current = false;
    fetchInProgress.current = false;
  }, []);

  // Helper function to fallback to cache if available
  const useCachedDataIfAvailable = useCallback(() => {
    if (cachedProjects.current && cachedProjects.current.length > 0) {
      setProjects(cachedProjects.current);
      return true;
    }
    return false;
  }, []);

  const fetchUserProfile = useCallback(async (isMountedCheck, userId) => {
    try {
      if (!userId || !isMountedCheck()) return;
      
      const { data, error } = await supabase
          .from('profiles')
          .select('full_name, email')
          .eq('id', userId)
          .single();

      if (error) throw error;
      if (isMountedCheck()) {
        setUserProfile(data);
      }
      return data;
    } catch (error) {
      console.error('[fetchUserProfile] Error:', error);
      return null;
    }
  }, []);

  const fetchUserProjects = useCallback(async (isMountedCheck) => {
    try {
      if (!isMountedCheck()) return;

      const { data, error } = await supabase
          .from('projects')
          .select(`
            id,
            name,
            created_at,
            documents:documents(
              id,
              status
            )
          `)
          .neq('status', 'archived')
          .order('created_at', { ascending: false });

      if (error) {
        console.error("[fetchUserProjects] Supabase error:", error);
        throw error;
      }

      if (isMountedCheck()) {
        const transformedData = data?.map(project => {
          const documents = project.documents || [];
          const totalDocuments = documents.length;
          const assessedDocuments = documents.filter(doc => doc.status === 'reviewed' || doc.status === 'exported').length;
          
          return {
            ...project,
            documentCount: totalDocuments,
            assessedCount: assessedDocuments,
            assessmentProgress: totalDocuments > 0 ? (assessedDocuments / totalDocuments) * 100 : 0
          };
        }) || [];
        
        setProjects(transformedData);
        return transformedData;
      }
    } catch (error) {
      console.error('[fetchUserProjects] Error:', error);
      return [];
    }
  }, []);

  // Function to trigger data refresh - Simplified
  const refreshData = useCallback(() => {
      console.log("Manual refresh triggered (currently relies on main useEffect reacting to user state)");
  }, []);

  // Main data loading effect
  useEffect(() => {
    let isMounted = true;
    const isMountedCheck = () => isMounted;
    let loadTimeout = null;

    const loadData = async () => {
      if (isLoadingData.current || fetchInProgress.current || authLoading || !user) {
        if (!user && !authLoading) {
           setProjectsLoading(false);
        }
        return;
      }

      try {
        isLoadingData.current = true;
        fetchInProgress.current = true;
        
        const hasCachedData = useCachedDataIfAvailable();
        
        if (isMountedCheck() && !hasCachedData) {
          setProjectsLoading(true);
        }
        
        if (globalTimeoutId.current) {
          clearTimeout(globalTimeoutId.current);
        }
        
        globalTimeoutId.current = setTimeout(() => {
          console.error("⚠️ Global loadData timeout reached! Forcing release of loading state.");
          if (isMountedCheck()) {
            setProjectsLoading(false);
          }
          resetConnection();
        }, 15000);

        try {
          await fetchUserProfile(isMountedCheck, user.id);
        } catch (profileError) {
        }

        if (isMountedCheck()) {
          try {
            await fetchUserProjects(isMountedCheck);
          } catch (projectsError) {
            console.error("Error in projects fetch:", projectsError);
          }
        }
      } catch (error) {
        console.error("Error during sequential loadData:", error);
      } finally {
        if (globalTimeoutId.current) {
          clearTimeout(globalTimeoutId.current);
          globalTimeoutId.current = null;
        }
        
        if (isMountedCheck()) {
          setProjectsLoading(false);
        }
        
        isLoadingData.current = false;
        fetchInProgress.current = false;
      }
    };

    loadData();

    const handleVisibilityChangeInternal = () => {
       if (document.visibilityState === 'visible') {
         const now = Date.now();
         const timeSinceLastChange = now - lastVisibilityChange.current;
         if (timeSinceLastChange > 1000 && !isLoadingData.current && !fetchInProgress.current) {
           lastVisibilityChange.current = now;
           loadData();
         }
       }
     };

    // document.addEventListener('visibilitychange', handleVisibilityChangeInternal); // Uitgecommentarieerd

    return () => {
      isMounted = false;
      // document.removeEventListener('visibilitychange', handleVisibilityChangeInternal); // Uitgecommentarieerd
      if (globalTimeoutId.current) {
        clearTimeout(globalTimeoutId.current);
        globalTimeoutId.current = null;
      }
      isLoadingData.current = false;
      fetchInProgress.current = false;
    };
  }, [user, authLoading, fetchUserProfile, fetchUserProjects, resetConnection, useCachedDataIfAvailable]); 

  // Overall loading combines auth and projects loading
  const combinedLoading = authLoading || projectsLoading;

  if (!user && !authLoading) {
    // If auth check is complete and still no user, redirect to login
    navigate('/login');
    return null;
  }

  return (
    <Container
      component={motion.div}
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5 }}
      sx={{ mt: 8, mb: 4 }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          mb: 4,
          mt: 2,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h4" gutterBottom>
          Welkom, {userProfile?.full_name || user?.email}!
        </Typography>
        <Typography variant="body1">
          Dit is je dashboard. Hier kun je je laatste activiteiten bekijken en rapporten beheren, etc.
        </Typography>
      </Paper>

      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'medium' }}>
        Jouw Projecten
      </Typography>

      {combinedLoading ? (
        <Box sx={{ width: '100%' }}>
          {[1, 2, 3].map((skeleton) => (
            <Skeleton key={skeleton} variant="rectangular" height={100} sx={{ mb: 2, borderRadius: 1 }} />
          ))}
        </Box>
      ) : projects.length > 0 ? (
        <Box sx={{ width: '100%' }}>
          {projects.map((project) => (
            <Card 
              key={project.id}
              elevation={2}
              component={motion.div}
              whileHover={{ scale: 1.01 }}
              transition={{ duration: 0.2 }}
              sx={{ mb: 2 }}
            >
              <CardActionArea onClick={() => navigate(`/projects?projectId=${project.id}`)}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {project.name}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Description sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="body2" color="text.secondary">
                        {project.documentCount} document{project.documentCount === 1 ? '' : 'en'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DateRange sx={{ mr: 1, color: 'primary.main' }} />
                      <Typography variant="body2" color="text.secondary">
                        {new Date(project.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">
                        Voortgang beoordeling
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <CheckCircle sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5, color: 'success.main' }} />
                        {project.assessedCount} / {project.documentCount} beoordeeld
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={project.assessmentProgress} 
                      sx={{ height: 8, borderRadius: 1 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Chip
                        label={`${Math.round(project.assessmentProgress)}%`}
                        size="small"
                        color={project.assessmentProgress === 100 ? "success" : "primary"}
                        sx={{ height: 20 }}
                      />
                      <Chip
                        label={`${project.documentCount - project.assessedCount} te beoordelen`}
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ height: 20 }}
                      />
                    </Box>
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      ) : (
        <Paper 
          elevation={1} 
          sx={{ 
            p: 3, 
            textAlign: 'center',
            backgroundColor: 'grey.50'
          }}
        >
          <FolderOpen sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography color="text.secondary">
            Je bent nog niet toegewezen aan projecten.
          </Typography>
        </Paper>
      )}
    </Container>
  );
}

export default Dashboard;
